import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card } from "primereact/card";
import { TabView, TabPanel } from "primereact/tabview";
import { Dialog } from "primereact/dialog";
import { InputTextarea } from "primereact/inputtextarea";
import { Calendar } from "primereact/calendar";
import { InputNumber } from "primereact/inputnumber";
import { Dropdown } from "primereact/dropdown";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { StatusBadge } from "../../components/ui/StatusBadge";
import { AppTheme } from "../../AppTheme";
import {
  CalculateInvoiceResultDto,
  GetInvoiceDto,
  InvoiceStatusEnum,
  RecordPaymentDto,
} from "../../types/invoice.types";
import {
  cloneInvoice,
  deleteInvoice,
  downloadInvoice,
  getInvoiceById,
  recordPayment,
  removeRecordPayment,
  sendInvoice,
  writeOffInvoice,
} from "../../service/invoice/InvoiceServices";
import moment from "moment";
import { useDispatch } from "react-redux";
import axios from "axios";
import { Button } from "../../components/ui/Button";
import { getTenantPaymentSettingsByTenantId } from "../../service/tenantPaymentSettings/TenantPaymentSettingsService";
import { getClientById } from "../../service/client/ClientServices";
import { getMailHistoryByInvoiceId } from "../../service/mailHistory/MailHistoryService";
import { getInvoicePaymentByInvoiceId } from "../../service/invoicePayments/InvoicePaymentService";
import { ClientDocumentDto, GetClientDto } from "@/types/client.types";
import {
  faDownload,
  faSquareUpRight,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { showToast } from "../../store/slices/toastSlice";
import { SendInvoiceDialog } from "../../components/ui/SendInvoiceDialog";
import DOMPurify from "dompurify";

import { ChevronDown, ChevronUp } from "lucide-react";
import { CalculateInvoice } from "../../shared/functions/CalculateInvoice";
import { getFromStorage } from "../../utils/storageUtils";

interface TruncatedTextColumnProps {
  text: string;
  charLimit?: number;
}

export const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState<GetInvoiceDto | null>(null);
  const [sendDialogVisible, setSendDialogVisible] = useState<boolean>(false);
  const [paymentDialogVisible, setPaymentDialogVisible] =
    useState<boolean>(false);
  const [writeOffDialogVisible, setWriteOffDialogVisible] =
    useState<boolean>(false);
  const [writeOffReason, setWriteOffReason] = useState<string>("");
  const [paymentAmount, setPaymentAmount] = useState<number | null>(null);
  const [paymentDate, setPaymentDate] = useState<Date | null>(new Date());
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null);
  const [paymentReference, setPaymentReference] = useState<string>("");
  const [paymentNotes, setPaymentNotes] = useState<string>("");
  const [emailMessage, setEmailMessage] = useState<string>("");
  const [emailTo, setEmailTo] = useState<string>("");
  const [emailSubject, setEmailSubject] = useState<string>("");
  const [emailCc, setEmailCc] = useState<string>("");
  const toastRef = React.useRef<Toast>(null);
  const dispatch = useDispatch();
  const [invoiceMailHistory, setInvoiceMailHistory] = useState<any>();
  const [invoicePaymentHistory, setInvoicePaymentHistory] = useState<any>();
  const [client, setClient] = useState<GetClientDto | null>(null);
  const [visible, setVisible] = useState(false);
  const [clickedItemIndex, setClickedItemIndex] = useState<number | null>(null);
  const [invoiceTotal, setInvoiceTotal] = useState<CalculateInvoiceResultDto>({
    taxTotal: 0,
    total: 0,
    totalDiscount: 0,
    subTotal: 0,
    sacTotal: 0,
  });
  const [withPaymentLink, setWithPaymentLink] = useState(false);
    const [isAccordionOpen, setIsAccordionOpen] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

  // Payment method options
  const paymentMethods = [
    { label: "Credit Card", value: "creditCard" },
    { label: "Bank Transfer", value: "bankTransfer" },
    { label: "PayPal", value: "paypal" },
    { label: "Cash", value: "cash" },
    { label: "Check", value: "check" },
  ];

  const handleInvoiceCalculation = () => {
    if (!invoice) return null;
    const result = CalculateInvoice(invoice);
    setInvoiceTotal(result);
  };

  useEffect(() => {
    fetchInvoice();
    getInvoiceMailHistory();
    getInvoicePayemntHistory();
  }, [id]);

  useEffect(() => {
    handleInvoiceCalculation();
  }, [invoice]);

  const getInvoicePayemntHistory = async () => {
    const paymentHistory = await getInvoicePaymentByInvoiceId(
      dispatch,
      id as unknown as string
    );
    setInvoicePaymentHistory(paymentHistory);
  };
  const getInvoiceMailHistory = async () => {
    const mailHistory = await getMailHistoryByInvoiceId(
      dispatch,
      id as unknown as number
    );
    setInvoiceMailHistory(mailHistory);
  };

  const fetchInvoice = async () => {
    if (!id) return;

    const invoiceData = await getInvoiceById(dispatch, id);
    invoiceData.choosedTemplateId = invoiceData.templateId;
    if (!invoiceData) return;
    setInvoice(invoiceData);
    

    // Pre-fill email fields
    if (invoiceData) {
      // setEmailTo(invoiceData.client_email || "");
      setEmailSubject(
        `Invoice ${invoiceData.invoice_number} from Your Company`
      );
      setEmailMessage(
        `Dear ${
          invoiceData.client_name
        },\n\nPlease find attached your invoice ${
          invoiceData.invoice_number
        } for ${invoiceData.currency_symbol} ${
          invoiceData.total
        }.\n\nPayment is due by ${new Date(
          invoiceData.due_date
        ).toLocaleDateString()}.\n\nThank you for your business.`
      );
      setPaymentAmount(invoiceData.balance_due);
    }
  };

  const getClientInfo = async (clientId: string) => {
    const clientInfo = await getClientById(dispatch, clientId);
    if (!clientInfo) return;
    setEmailTo(clientInfo?.email);
    setClient(clientInfo);
  };
  useEffect(() => {
    if (invoice?.client_id) {
      getClientInfo(invoice?.client_id.toString());
    }
  }, [invoice?.client_id]);

  const paymentSettingChecker = async () => {
    const tenantId = localStorage.getItem("tenantId");
    if (!tenantId) return;
    const isSettingsExist = await getTenantPaymentSettingsByTenantId(
      dispatch,
      tenantId
    );
    if (isSettingsExist) {
      setWithPaymentLink(true);
    } else {
      setWithPaymentLink(false);
    }
  };

  useEffect(() => {
    paymentSettingChecker();
  }, []);

  const handleSendInvoice = async (propsEmailTo:string,propsEmailCc:string,template_id?: number) => {
    if (!id || !invoice) return;

    setEmailCc(propsEmailCc);
    setEmailTo(propsEmailTo);

    if (propsEmailTo.length == 0) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Email recipients is required!",
          life: 3000,
        })
      );
      return;
    }

    try {
      await sendInvoice(dispatch, id, {
        to: [propsEmailTo],
        cc: emailCc ? [propsEmailCc] : [],
        subject: emailSubject,
        message: emailMessage,
        clientId: client?.client_id,
        clientName: client?.client_name,
        withPaymentLink,
        choosedTemplateId: template_id,
      });
      getInvoiceMailHistory();
      setSendDialogVisible(false);
      dispatch(
        showToast({
          severity: "success",
          summary: "Success",
          detail: "Invoice sent successfully",
          life: 3000,
        })
      );

      // Refresh invoice data to get updated status
      fetchInvoice();
    } catch (error) {
      console.error("Error sending invoice:", error);
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to send invoice",
          life: 3000,
        })
      );
    }
  };

  const handleRecordPayment = async () => {
    if (!id || !invoice || !paymentAmount || !paymentDate || !paymentMethod)
      return;

    if (invoice?.balance_due < paymentAmount) {
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Payment amount cannot exceed balance due",
          life: 3000,
        })
      );
      return;
    }

    try {
      const paymentData: RecordPaymentDto = {
        amount: paymentAmount,
        payment_date: paymentDate,
        payment_method: paymentMethod,
        payment_reference: paymentReference || undefined,
        notes: paymentNotes || undefined,
      };

      const res = await recordPayment(dispatch, id, paymentData);
      getInvoicePayemntHistory();
      setPaymentDialogVisible(false);
      dispatch(
        showToast({
          severity: "success",
          summary: "Success",
          detail: "Payment recorded successfully",
          life: 3000,
        })
      );

      // Refresh invoice data to get updated status
      fetchInvoice();
    } catch (error) {
      console.error("Error recording payment:", error);
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to record payment",
          life: 3000,
        })
      );
    }
  };

  const handleWriteOff = async () => {
    if (!id || !invoice || !writeOffReason.trim()) return;

    try {
      await writeOffInvoice(dispatch, id, writeOffReason);
      setWriteOffDialogVisible(false);
      dispatch(
        showToast({
          severity: "success",
          summary: "Success",
          detail: "Invoice written off successfully",
          life: 3000,
        })
      );

      // Refresh invoice data to get updated status
      fetchInvoice();
    } catch (error) {
      console.error("Error writing off invoice:", error);
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to write off invoice",
          life: 3000,
        })
      );
    }
  };

  // Frontend API function to handle Buffer response
  const handleDownloadInvoice = async (invoiceId: string, format = "pdf") => {
    try {
      // Show loader or set loading state if needed
      const authHeaders = {
        access_token: localStorage.getItem("token"),
        tenantId: localStorage.getItem("tenantId"),
      };

      // Direct axios call with proper configuration for binary data
      const response = await axios({
        url: `${process.env.REACT_APP_BASE_URL}/invoices/${invoiceId}/download?format=${format}`,
        method: "GET",
        responseType: "arraybuffer",
        headers: authHeaders,
      });

      // Handle the binary response
      const contentType =
        response.headers["content-type"] ||
        (format === "pdf" ? "application/pdf" : "application/octet-stream");

      // Create a blob from the binary data
      const blob = new Blob([response.data], { type: contentType });

      // Create a download link
      const fileName = `invoice-${
        invoice?.invoice_number || "download"
      }.${format}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();

      // Clean up
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);

      dispatch(
        showToast({
          severity: "success",
          summary: "Success",
          detail: `Invoice downloaded as ${format.toUpperCase()}`,
          life: 3000,
        })
      );
    } catch (error) {
      console.error("Error downloading invoice:", error);
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to download invoice",
          life: 3000,
        })
      );
    }
  };

  const handleCloneInvoice = async () => {
    if (!id) return;

    try {
      const result = await cloneInvoice(dispatch, id);

      // Navigate to the new invoice
      if (result && result.id) {
        dispatch(
          showToast({
            severity: "success",
            summary: "Success",
            detail: "Invoice cloned successfully",
            life: 3000,
          })
        );
        navigate(`/invoice/${result.id}`);
      }
    } catch (error) {
      console.error("Error cloning invoice:", error);
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to clone invoice",
          life: 3000,
        })
      );
    }
  };

  const confirmDeleteInvoice = () => {
    confirmDialog({
      message: "Are you sure you want to delete this invoice?",
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: handleDeleteInvoice,
    });
  };

  const confirmWriteOff = () => {
    setWriteOffReason("");
    setWriteOffDialogVisible(true);
  };

  const handleDeleteInvoice = async () => {
    if (!id) return;

    try {
      await deleteInvoice(dispatch, id);

      dispatch(
        showToast({
          severity: "success",
          summary: "Success",
          detail: "Invoice deleted successfully",
          life: 3000,
        })
      );

      // Navigate back to invoice list
      navigate("/invoices");
    } catch (error) {
      console.error("Error deleting invoice:", error);
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete invoice",
          life: 3000,
        })
      );
    }
  };

  // New function to handle deleting a payment
  const confirmDeletePayment = (rowData: any) => {
    confirmDialog({
      message: "Are you sure you want to delete this payment history?",
      header: "Confirm Deletion",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => handleDeletePayment(rowData),
    });
  };

  // Function to handle the actual payment deletion
  const handleDeletePayment = async (rowData: any) => {
    if (rowData?.payment_method == "stripe") {
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "You can't delete any stripe payment history!",
          life: 3000,
        })
      );
      return;
    }
    try {
      await removeRecordPayment(dispatch, rowData?.id);

      dispatch(
        showToast({
          severity: "success",
          summary: "Success",
          detail: "Payment deleted successfully",
          life: 3000,
        })
      );

      // Refresh payment history and invoice data
      getInvoicePayemntHistory();
      fetchInvoice();
    } catch (error) {
      console.error("Error deleting payment:", error);
      dispatch(
        showToast({
          severity: "error",
          summary: "Error",
          detail: "Failed to delete payment",
          life: 3000,
        })
      );
    }
  };

  const openSendDialog = async () => {
    if (invoice) {
      setSendDialogVisible(true);
    }
  };

  const openPaymentDialog = () => {
    if (invoice) {
      setPaymentAmount(invoice.balance_due);
      setPaymentDate(new Date());
      setPaymentMethod(null);
      setPaymentReference("");
      setPaymentNotes("");
      setPaymentDialogVisible(true);
    }
  };

  if (!invoice) {
    return <div className="p-4">Invoice not found</div>;
  }

  const writeOffDialogFooter = (
    <div>
      <Button
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setWriteOffDialogVisible(false)}
      >
        Cancel
      </Button>
      <Button
        icon="pi pi-check"
        onClick={handleWriteOff}
        disabled={!writeOffReason.trim()}
        className="p-button-danger"
      >
        Write Off
      </Button>
    </div>
  );

  const TruncatedTextColumn = ({
    text,
    charLimit = 50,
  }: TruncatedTextColumnProps) => {
    const [expanded, setExpanded] = useState(false);

    const truncateText = (text: string, limit: number) => {
      if (!text) return "";
      if (text?.length <= limit) return text;
      return text.slice(0, limit);
    };

    const truncatedText = truncateText(text, charLimit);
    const isTruncated = text && text?.length > charLimit;

    return (
      <div>
        {expanded || !isTruncated ? text : truncatedText}
        {isTruncated && (
          <span
            onClick={() => setExpanded(!expanded)}
            style={{
              color: "blue",
              cursor: "pointer",
              marginLeft: "4px",
            }}
          >
            {expanded ? "" : "..."}
          </span>
        )}
      </div>
    );
  };

  // Template for delete action column
  const actionBodyTemplate = (rowData: any) => {
    return (
      <Button
        icon="pi pi-trash"
        className="p-button-rounded p-button-danger p-button-text"
        onClick={() => confirmDeletePayment(rowData)}
      >
        <FontAwesomeIcon icon={faTrash} />
      </Button>
    );
  };

  const paymentDialogFooter = (
    <div>
      <Button
        icon="pi pi-times"
        className="p-button-text"
        onClick={() => setPaymentDialogVisible(false)}
      >
        Cancel
      </Button>
      <Button
        icon="pi pi-check"
        onClick={handleRecordPayment}
        disabled={!paymentAmount || !paymentDate || !paymentMethod}
      >
        Record Payment
      </Button>
    </div>
  );

  // Document list view
  const renderDocuments = () => {
    if (!invoice?.documents || invoice.documents?.length === 0) {
      return (
        <div className="text-gray-500 text-center py-4">
          No documents found for this client.
        </div>
      );
    }

    // Function to handle document download
    const handleDocumentDownload = (doc: ClientDocumentDto) => {
      if (doc.base64Content) {
        // Get proper file extension and name
        const fileName =
          doc.file_name || `Document_${doc.id || new Date().getTime()}`;
        const fileExt = fileName.split(".").pop()?.toLowerCase() || "pdf";
        const finalFileName = fileName.includes(".")
          ? fileName
          : `${fileName}.${fileExt}`;

        // Process base64 content - remove data URI prefix if present
        let base64Data = doc.base64Content;
        if (base64Data.includes(";base64,")) {
          base64Data = base64Data.split(";base64,")[1];
        }

        // Determine MIME type
        let mimeType = doc.file_type || "application/pdf";
        if (finalFileName.toLowerCase().endsWith(".pdf")) {
          mimeType = "application/pdf";
        } else if (finalFileName.toLowerCase().match(/\.(jpg|jpeg|png|gif)$/)) {
          mimeType = `image/${fileExt}`;
        }

        // Convert base64 to blob - use Uint8Array directly for binary files
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString?.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }

        const blob = new Blob([bytes], { type: mimeType });

        // Create and trigger download
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = finalFileName;
        document.body.appendChild(a);
        a.click();

        // Cleanup
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(a);
        }, 100);
      } else if (doc.file_path) {
        // Handle file_path download
        window.open(doc.file_path, "_blank");
      } else {
        return;
      }
    };

    return (
      <div className="space-y-2">
        {invoice.documents.map((doc: ClientDocumentDto, index: number) => {
          const fileName =
            doc.file_name ||
            (doc.file_path
              ? doc.file_path.split("/").pop()
              : `Document ${index + 1}`);
          const fileExtension = fileName?.split(".").pop()?.toLowerCase() || "";

          // Determine file type icon
          let fileIcon = faDownload;
          if (["pdf"].includes(fileExtension)) {
            fileIcon = faDownload;
          } else if (["jpg", "jpeg", "png", "gif"].includes(fileExtension)) {
            fileIcon = faDownload;
          }

          return (
            <div
              key={index}
              className="flex items-center justify-between p-3 bg-gray-50 rounded-md"
            >
              <span className="flex items-center">
                <FontAwesomeIcon
                  icon={fileIcon}
                  className="text-gray-500 mr-2"
                />
                {fileName}
              </span>
              <div className="flex gap-2">
                <Button
                  className="p-button-sm p-button-text"
                  onClick={() => handleDocumentDownload(doc)}
                >
                  <FontAwesomeIcon icon={faDownload} /> Download
                </Button>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const previewTemplate = (rowData: any) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(rowData.mail_body, "text/html");

    // Modify styles dynamically
    doc.querySelectorAll("table").forEach((table) => {
      table.style.marginTop = "0px"; // Adjust margin-top dynamically
      table.style.borderCollapse = "collapse"; // Ensure border collapse for table
      table.style.width = "fit-content"; // Adjust width dynamically
    });
    doc.querySelectorAll("p").forEach((p) => {
      p.style.fontSize = "15px"; // Adjust font size dynamically
    });
    doc.querySelectorAll("th").forEach((th) => {
      th.style.fontSize = "15px"; // Adjust table header font size
      th.style.backgroundColor = "#2E6198"; // Background color for headers
      th.style.color = "white"; // Text color for headers
      th.style.padding = "0.8rem 8rem"; // Padding for header
      th.style.textAlign = "center"; // Text alignment for header
    });
    doc.querySelectorAll("td").forEach((td) => {
      td.style.fontSize = "15px"; // Adjust table data font size
      td.style.padding = "0.8rem 8rem"; // Padding for table data
      td.style.textAlign = "center"; // Text alignment for data cells
      td.style.border = "1px solid #7D7C7C"; // Border style for data cells
    });

    // Serialize the full document (including the <head><style> block, which
    // holds the template's CSS) and sanitize it while keeping the style tags,
    // then render it inside an isolated iframe so the email markup displays
    // exactly as it was designed instead of as unstyled, stacked text.
    const sanitizedHtml = DOMPurify.sanitize(doc.documentElement.outerHTML, {
      WHOLE_DOCUMENT: true,
      ADD_TAGS: ["style"],
    });

    return (
      <div className="d-flex justify-content-center align-items-center">
        <Button
          icon="pi pi-arrow-up-right"
          onClick={() => {
            setVisible(true);
            setClickedItemIndex(rowData.id);
          }}
        >
          <FontAwesomeIcon icon={faSquareUpRight} />
        </Button>
        {visible && clickedItemIndex === rowData.id && (
          <Dialog
            header="Mail Content"
            visible={visible}
            style={{ width: "50vw" }}
            onHide={() => {
              if (!visible) return;
              setVisible(false);
            }}
          >
            <iframe
              title="Mail Content"
              srcDoc={sanitizedHtml}
              sandbox=""
              style={{
                width: "100%",
                minHeight: "70vh",
                border: "none",
              }}
            />
          </Dialog>
        )}
      </div>
    );
  };

  return (
    <div>
      <Toast ref={toastRef} />
      <ConfirmDialog />

      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <h1
            className={`text-${AppTheme.font.size["2xl"]} font-bold text-gray-800`}
          >
            Invoice {invoice.invoice_number}
          </h1>
          <StatusBadge status={invoice.status} />
        </div>

        <div className="flex gap-2">
          <Button
            icon="pi pi-pencil"
            className="p-button-outlined"
            onClick={() => navigate(`/invoice/edit/${id}`)}
            disabled={
               invoice.status?.toLowerCase() !== InvoiceStatusEnum.DRAFT?.toLowerCase()
            }
          >
            Edit
          </Button>
          <Button
            icon="pi pi-send"
            onClick={openSendDialog}
            disabled={
              invoice.status === InvoiceStatusEnum.PAID ||
              invoice.status === InvoiceStatusEnum.CANCELLED ||
              invoice.status === InvoiceStatusEnum.WRITE_OFF
            }
          >
            Send
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <Card className="col-span-2">
          <div className="p-4">
            <div className="flex justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold mb-2">Client</h2>
                <p className="font-medium">{invoice.client_name}</p>
                {client && (
                  <div>
                    <p className="text-gray-600">Email: {client?.email}</p>
                    <p className="text-gray-600">Phone: {client?.phone}</p>
                    <p className="text-gray-600">
                      Address: {client?.billing_address?.address_line1},
                      {client?.billing_address?.state},{" "}
                      {client?.billing_address?.country_name}
                    </p>
                  </div>
                )}
              </div>
              <div>
                <h2 className="text-xl font-semibold mb-2">Amount</h2>
                <p className="text-2xl font-bold">
                  {invoice.currency_symbol} {Number(invoice?.total)?.toFixed(2)}
                </p>
                {invoice.balance_due > 0 && (
                  <p className="text-gray-600">
                    Balance Due: {invoice.currency_symbol}{" "}
                    {Number(invoice.balance_due || 0).toFixed(2)}
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <div>
                <h2 className="text-lg font-semibold mb-2">Invoice Details</h2>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-600">Invoice Number:</div>
                  <div>{invoice.invoice_number}</div>
                  <div className="text-gray-600">Issue Date:</div>
                  <div>{new Date(invoice.issue_date).toLocaleDateString()}</div>
                  <div className="text-gray-600">Due Date:</div>
                  <div>{new Date(invoice.due_date).toLocaleDateString()}</div>
                  <div className="text-gray-600">Industry:</div>
                  <div>{invoice.industry_name || "N/A"}</div>
                </div>
              </div>
              <div>
                <h2 className="text-lg font-semibold mb-2">Payment Details</h2>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-gray-600">Status:</div>
                  <div>
                    <StatusBadge status={invoice.status} />
                  </div>
                  <div className="text-gray-600">Payment Terms:</div>
                  <div>{invoice.payment_terms || "N/A"}</div>
                  <div className="text-gray-600">Currency:</div>
                  <div>{invoice.currency_code}</div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <Card>
          <div className="p-4">
            <h2 className="text-xl font-semibold mb-4">Actions</h2>
            <div className="flex flex-col gap-2">
              <Button
                icon="pi pi-dollar"
                className="w-full"
                style={{
                  background: "var(--primaryColor)",
                  border: "1px solid var(--primaryColor)",
                  color: "#fff",
                }}
                onClick={openPaymentDialog}
                disabled={
                  invoice.status === InvoiceStatusEnum.DRAFT ||
                  invoice.status === InvoiceStatusEnum.PAID ||
                  invoice.status === InvoiceStatusEnum.WRITE_OFF
                }
              >
                Record Payment
              </Button>
              <Button
                icon="pi pi-minus-circle"
                className="p-button-outlined w-full"
                onClick={confirmWriteOff}
                disabled={
                  invoice.status === InvoiceStatusEnum.DRAFT ||
                  invoice.status === InvoiceStatusEnum.PAID ||
                  invoice.status === InvoiceStatusEnum.WRITE_OFF
                }
              >
                Write Off
              </Button>
              <Button
                icon="pi pi-download"
                className="p-button-outlined w-full surface-900"
                onClick={() => handleDownloadInvoice(id as string, "pdf")}
              >
                Download PDF
              </Button>
              <Button
                icon="pi pi-copy"
                className="p-button-outlined w-full"
                onClick={handleCloneInvoice}
              >
                Clone Invoice
              </Button>
              <Button
                icon="pi pi-trash"
                className="p-button-danger w-full"
                onClick={confirmDeleteInvoice}
                disabled={invoice.status !== InvoiceStatusEnum.DRAFT}
              >
                Delete
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <TabView>
        <TabPanel header="Items">
          <DataTable value={invoice.invoice_items} stripedRows>
            <Column
              field="item_name"
              header="Name"
              body={(rowdata) => (rowdata.item_name ? rowdata?.item_name : "-")}
            />
            <Column
              field="item_code"
              header="Code"
              body={(rowdata) => (rowdata.item_code ? rowdata?.item_code : "-")}
            />
            <Column field="quantity" header="Quantity" />
            <Column
              field="unit_price"
              header="Unit Price"
              body={(rowData) =>
                `${invoice.currency_symbol} ${rowData.unit_price}`
              }
            />
            <Column
              field="tax_rate"
              header="Tax Rate"
              body={(rowData) =>
                rowData.sac_gst_rate
                  ? `${rowData.sac_gst_rate}% ${
                      rowData?.sacCode ? `(SAC: ${rowData.sacCode})` : ""
                    }`
                  : "N/A"
              }
            />
            <Column
              field="discount"
              header="Discount"
              body={(rowData) =>
                // rowData.discount ? `${rowData.discount}%` : "N/A"
                rowData.discount !== null && rowData.discount !== undefined ? `${rowData.discount}%` : "N/A"
              }
            />
            <Column
              field="line_total"
              header="Line Total"
              body={(rowData) => {
                const discountAmount =
                  (rowData.line_total * rowData.discount) / 100;
                const discountedTotal = rowData.line_total - discountAmount;
                return `${invoice.currency_symbol} ${discountedTotal.toFixed(
                  2
                )}`;
              }}
            />
          </DataTable>

          <div className="flex justify-end mt-4">
            <div className="w-64">
              {/* <div className="flex justify-between py-2">
                <span className="text-gray-600">Subtotal:</span>
                <span>
                  {invoice.currency_symbol} {invoiceTotal.subTotal}
                </span>
              </div> */}
              <div className="border-b border-gray-200">
                {/* Clickable Sub Total Header */}
                <div
                  className="flex items-center justify-between py-3 cursor-pointer hover:bg-gray-50 transition-colors relative"
                  onClick={() => setIsAccordionOpen(!isAccordionOpen)}
                  onMouseEnter={() => setShowTooltip(true)}
                  onMouseLeave={() => setShowTooltip(false)}
                >
                  <div className="flex items-center space-x-2">
                    <span className="font-medium text-gray-700">Sub Total</span>
                    {isAccordionOpen ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </div>
                  <span className="font-semibold text-gray-700">{invoice.currency_symbol} {invoiceTotal.subTotal}</span>
                  {/* Tooltip */}
                  {showTooltip && !isAccordionOpen && (
                    <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded-md shadow-lg z-10 whitespace-nowrap">
                      Tax breakdown
                      <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-2 h-2 bg-gray-800 rotate-45"></div>
                    </div>
                  )}
                </div>
                {/* Tax Breakdown Content */}
                {isAccordionOpen && (
                  <div className="bg-gray-50 pb-3">
                    <div className="px-4 space-y-2">
                      {invoice.invoice_items.length > 0 ?
                        invoice.invoice_items.map(({ taxName,tax_amount,sac_gst_rate  }) => (
                          <div className="flex justify-between py-1 text-sm text-gray-600">
                            <span className="pl-4">
                              {taxName ?
                                `${taxName} [${sac_gst_rate}%]` :
                                `${sac_gst_rate}% [${sac_gst_rate}%]`
                              }
                            </span>
                            <span className="ml-2">{tax_amount.toFixed(2)}</span>
                          </div>
                        )) :
                        <span className="text-sm flex justify-center p-2">No tax breakdown availble</span>}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-between py-2">
                <span className="text-gray-600">Total Service Tax:</span>
                <span>
                  {invoice.currency_symbol} {invoiceTotal.sacTotal.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">Total Discount:</span>
                <span>
                  {invoice.currency_symbol} {invoiceTotal.totalDiscount}
                </span>
              </div>
              <div className="flex justify-between py-2">
                <span className="text-gray-600">
                  Total Tax{" "}
                  {invoice?.gstRate
                    ? `(${invoice?.gstType} : ${invoice?.gstRate}%)`
                    : "(TDS/TCS)"}
                  :
                </span>
                <span>
                  {invoice.currency_symbol} {invoiceTotal.taxTotal}
                </span>
              </div>

              <div className="flex justify-between py-2 font-bold border-t border-gray-200 mt-2 pt-2">
                <span>Total:</span>
                <span>
                  {invoice.currency_symbol} {invoiceTotal.total}
                </span>
              </div>
              {invoice.amount_paid > 0 && (
                <div className="flex justify-between py-2 text-gray-600">
                  <span>Amount Paid:</span>
                  <span>
                    {invoice.currency_symbol} {invoice.amount_paid}
                  </span>
                </div>
              )}
              {invoice.balance_due > 0 && (
                <div className="flex justify-between py-2 font-bold text-gray-800">
                  <span>Balance Due:</span>
                  <span>
                    {invoice.currency_symbol} {invoice.balance_due}
                  </span>
                </div>
              )}
            </div>
          </div>
        </TabPanel>

        <TabPanel header="Notes">
          <div className="p-4">
            <h2 className="text-lg font-semibold mb-2">Invoice Notes</h2>
            <p>{invoice.notes || "No notes"}</p>

            {invoice.terms_conditions && (
              <>
                <h2 className="text-lg font-semibold mb-2 mt-4">
                  Terms & Conditions
                </h2>
                <p>{invoice.terms_conditions}</p>
              </>
            )}
          </div>
        </TabPanel>

        <TabPanel header="Documents">{renderDocuments()}</TabPanel>

        <TabPanel header="Mail History">
          <div className="p-4">
            {invoiceMailHistory?.length === 0 ? (
              <p className="text-gray-500 italic">No mail history available.</p>
            ) : (
              <DataTable value={invoiceMailHistory} stripedRows>
                <Column field="client_name" header="Client Name" />
                <Column field="recipient_mail_address" header="Email Address" />
                <Column
                  field="createdAt"
                  header="Date"
                  body={(rowData) =>
                    moment(rowData.createdAt).format("DD-MM-YYYY hh:mm A")
                  }
                />
                <Column
                  style={{ minWidth: "12rem" }}
                  field="mail_body"
                  header="Preview"
                  body={previewTemplate}
                ></Column>
              </DataTable>
            )}
          </div>
        </TabPanel>
        <TabPanel header="Payments History">
          <DataTable value={invoicePaymentHistory} stripedRows>
            <Column field="payment_date" header="Payment Date" />
            <Column field="payment_method" header="Payment Method" />
            <Column
              field="notes"
              header="Notes"
              body={(rowData) => (
                <TruncatedTextColumn
                  text={rowData.notes || "-"}
                  charLimit={50}
                />
              )}
            />
            <Column
              field="amount"
              header="Amount"
              body={(rowData) =>
                `${invoice.currency_symbol} ${
                  rowData?.amount ? Number(rowData.amount)?.toFixed(2) : 0
                }`
              }
            />
            <Column
              body={actionBodyTemplate}
              header="Actions"
              style={{ width: "8rem", textAlign: "center" }}
            />
          </DataTable>
        </TabPanel>
      </TabView>

      {/* Send Invoice Dialog */}
      <SendInvoiceDialog
        emailTo={emailTo}
        setEmailTo={setEmailTo}
        handleSendInvoice={handleSendInvoice}
        sendDialogVisible={sendDialogVisible}
        setSendDialogVisible={setSendDialogVisible}
        emailMessage={emailMessage}
        setEmailMessage={setEmailMessage}
        emailCc={emailCc}
        setEmailCc={setEmailCc}
        emailSubject={emailSubject}
        setEmailSubject={setEmailSubject}
        withPaymentLink={withPaymentLink}
        setWithPaymentLink={setWithPaymentLink}
        invoiceData={invoice}
        total={invoice?.total || 0}
        setInvoiceData={setInvoice}
      />

      {/* Record Payment Dialog */}
      <Dialog
        header="Record Payment"
        visible={paymentDialogVisible}
        onHide={() => setPaymentDialogVisible(false)}
        footer={paymentDialogFooter}
        style={{ width: "30vw" }}
      >
        <div className="p-fluid">
          <div className="mb-4">
            <label
              htmlFor="paymentAmount"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Amount
            </label>
            <InputNumber
              id="paymentAmount"
              value={paymentAmount}
              onValueChange={(e) => setPaymentAmount(e.value as number)}
              mode="currency"
              currency={invoice.currency_code}
              locale="en-US"
              max={invoice.balance_due}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="paymentDate"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Payment Date
            </label>
            <Calendar
              id="paymentDate"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.value as Date)}
              showIcon
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="paymentMethod"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Payment Method
            </label>
            <Dropdown
              id="paymentMethod"
              value={paymentMethod}
              options={paymentMethods}
              onChange={(e) => setPaymentMethod(e.value)}
              placeholder="Select payment method"
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="paymentReference"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reference (Optional)
            </label>
            <InputTextarea
              id="paymentReference"
              value={paymentReference}
              onChange={(e) => setPaymentReference(e.target.value)}
              rows={2}
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="paymentNotes"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Notes (Optional)
            </label>
            <InputTextarea
              id="paymentNotes"
              value={paymentNotes}
              onChange={(e) => setPaymentNotes(e.target.value)}
              rows={2}
            />
          </div>
        </div>
      </Dialog>

      {/* Write Off Dialog */}
      <Dialog
        header="Write Off Invoice"
        visible={writeOffDialogVisible}
        onHide={() => setWriteOffDialogVisible(false)}
        footer={writeOffDialogFooter}
        style={{ width: "30vw" }}
      >
        <div className="p-fluid">
          <div className="mb-4">
            <label
              htmlFor="writeOffReason"
              className="block text-sm font-medium text-gray-700 mb-1"
            >
              Reason for Write-Off <span className="text-red-500">*</span>
            </label>
            <InputTextarea
              id="writeOffReason"
              value={writeOffReason}
              onChange={(e) => setWriteOffReason(e.target.value)}
              rows={4}
              className={!writeOffReason.trim() ? "p-invalid" : ""}
              placeholder="Please provide a reason for writing off this invoice"
            />
            {!writeOffReason.trim() && (
              <small className="p-error">Reason is required</small>
            )}
          </div>
          <p className="text-sm text-gray-600">
            Writing off this invoice will mark it as uncollectible. This action
            cannot be undone.
          </p>
        </div>
      </Dialog>
    </div>
  );
};
